import { blendFuncInvMap, blendFuncMap, blendEquationInvMap, blendEquationMap, faceModeInvMap, faceModeMap, compareFuncInvMap, compareFuncMap, stencilOpInvMap, stencilOpMap } from './constants_webgl.js';
import { WebGLEnum } from './webgl_enum.js';

class WebGLRenderState {
    static _defaultState;
    static _currentState;
    apply(gl, force) {
        const c = this.constructor;
        if (force || c._currentState !== this) {
            this._apply(gl);
        }
        c._currentState = this;
    }
    static get defaultState() {
        return WebGLRenderState._defaultState;
    }
    static applyDefaults(gl, force) {
        if (force || this._currentState !== this._defaultState) {
            this._defaultState.apply(gl, force);
        }
    }
}
class WebGLColorState extends WebGLRenderState {
    static _defaultState = new WebGLColorState();
    static _currentState = null;
    redMask;
    greenMask;
    blueMask;
    alphaMask;
    constructor(){
        super();
        this.redMask = this.greenMask = this.blueMask = this.alphaMask = true;
    }
    clone() {
        return new WebGLColorState().setColorMask(this.redMask, this.greenMask, this.blueMask, this.alphaMask);
    }
    setColorMask(r, g, b, a) {
        this.redMask = r;
        this.greenMask = g;
        this.blueMask = b;
        this.alphaMask = a;
        return this;
    }
    _apply(gl) {
        gl.colorMask(this.redMask, this.greenMask, this.blueMask, this.alphaMask);
    }
}
class WebGLBlendingState extends WebGLRenderState {
    static _defaultState = new WebGLBlendingState();
    static _currentState = null;
    _srcBlendRGB;
    _dstBlendRGB;
    _srcBlendAlpha;
    _dstBlendAlpha;
    _rgbEquation;
    _alphaEquation;
    enabled;
    alphaToCoverageEnabled;
    constructor(){
        super();
        this.enabled = false;
        this.alphaToCoverageEnabled = false;
        this.srcBlendRGB = 'one';
        this.dstBlendRGB = 'zero';
        this.srcBlendAlpha = 'one';
        this.dstBlendAlpha = 'zero';
        this.rgbEquation = 'add';
        this.alphaEquation = 'add';
    }
    clone() {
        const other = new WebGLBlendingState();
        other.enable(this.enabled);
        other.enableAlphaToCoverage(this.alphaToCoverageEnabled);
        other.setBlendFuncRGB(this.srcBlendRGB, this.dstBlendRGB);
        other.setBlendFuncAlpha(this.srcBlendAlpha, this.dstBlendAlpha);
        other.setBlendEquation(this.rgbEquation, this.alphaEquation);
        return other;
    }
    get srcBlendRGB() {
        return blendFuncInvMap[this._srcBlendRGB];
    }
    set srcBlendRGB(val) {
        this._srcBlendRGB = blendFuncMap[val];
    }
    get dstBlendRGB() {
        return blendFuncInvMap[this._dstBlendRGB];
    }
    set dstBlendRGB(val) {
        this._dstBlendRGB = blendFuncMap[val];
    }
    get srcBlendAlpha() {
        return blendFuncInvMap[this._srcBlendAlpha];
    }
    set srcBlendAlpha(val) {
        this._srcBlendAlpha = blendFuncMap[val];
    }
    get dstBlendAlpha() {
        return blendFuncInvMap[this._dstBlendAlpha];
    }
    set dstBlendAlpha(val) {
        this._dstBlendAlpha = blendFuncMap[val];
    }
    get rgbEquation() {
        return blendEquationInvMap[this._rgbEquation];
    }
    set rgbEquation(val) {
        this._rgbEquation = blendEquationMap[val];
    }
    get alphaEquation() {
        return blendEquationInvMap[this._alphaEquation];
    }
    set alphaEquation(val) {
        this._alphaEquation = blendEquationMap[val];
    }
    enable(b) {
        this.enabled = !!b;
        return this;
    }
    enableAlphaToCoverage(b) {
        this.alphaToCoverageEnabled = !!b;
        return this;
    }
    setBlendFunc(src, dest) {
        this.srcBlendRGB = src;
        this.dstBlendRGB = dest;
        this.srcBlendAlpha = src;
        this.dstBlendAlpha = dest;
        return this;
    }
    setBlendFuncRGB(src, dest) {
        this.srcBlendRGB = src;
        this.dstBlendRGB = dest;
        return this;
    }
    setBlendFuncAlpha(src, dest) {
        this.srcBlendAlpha = src;
        this.dstBlendAlpha = dest;
        return this;
    }
    setBlendEquation(rgb, alpha) {
        this.rgbEquation = rgb;
        this.alphaEquation = alpha;
        return this;
    }
    _apply(gl) {
        if (this.enabled) {
            gl.enable(WebGLEnum.BLEND);
            gl.blendEquationSeparate(this._rgbEquation, this._alphaEquation);
            if (this._srcBlendRGB === this._srcBlendAlpha && this._dstBlendRGB === this._dstBlendAlpha) {
                gl.blendFunc(this._srcBlendRGB, this._dstBlendRGB);
            } else {
                gl.blendFuncSeparate(this._srcBlendRGB, this._dstBlendRGB, this._srcBlendAlpha, this._dstBlendAlpha);
            }
        } else {
            gl.disable(WebGLEnum.BLEND);
        }
        if (this.alphaToCoverageEnabled) {
            gl.enable(WebGLEnum.SAMPLE_ALPHA_TO_COVERAGE);
        } else {
            gl.disable(WebGLEnum.SAMPLE_ALPHA_TO_COVERAGE);
        }
    }
}
class WebGLRasterizerState extends WebGLRenderState {
    static _defaultState = new WebGLRasterizerState();
    static _currentState = null;
    _cullMode;
    constructor(){
        super();
        this.cullMode = 'back';
    }
    clone() {
        return new WebGLRasterizerState().setCullMode(this.cullMode);
    }
    get cullMode() {
        return faceModeInvMap[this._cullMode];
    }
    set cullMode(val) {
        this._cullMode = faceModeMap[val];
    }
    setCullMode(mode) {
        this.cullMode = mode;
        return this;
    }
    get depthClampEnabled() {
        return false;
    }
    set depthClampEnabled(val) {
        this.enableDepthClamp(val);
    }
    enableDepthClamp(enable) {
        if (enable) {
            console.error('Depth clamp not supported');
        }
        return this;
    }
    _apply(gl) {
        if (this.cullMode == 'none') {
            gl.disable(WebGLEnum.CULL_FACE);
        } else {
            gl.enable(WebGLEnum.CULL_FACE);
            gl.cullFace(this._cullMode);
        }
    }
}
class WebGLDepthState extends WebGLRenderState {
    static _defaultState = new WebGLDepthState();
    static _currentState = null;
    testEnabled;
    writeEnabled;
    depthBias;
    depthBiasSlopeScale;
    _compareFunc;
    constructor(){
        super();
        this.testEnabled = true;
        this.writeEnabled = true;
        this.compareFunc = 'le';
        this.depthBias = 0;
        this.depthBiasSlopeScale = 0;
    }
    clone() {
        const other = new WebGLDepthState();
        other.enableTest(this.testEnabled);
        other.enableWrite(this.writeEnabled);
        other.setCompareFunc(this.compareFunc);
        other.setDepthBias(this.depthBias);
        other.setDepthBiasSlopeScale(this.depthBiasSlopeScale);
        return other;
    }
    get compareFunc() {
        return compareFuncInvMap[this._compareFunc];
    }
    set compareFunc(val) {
        this._compareFunc = compareFuncMap[val];
    }
    enableTest(b) {
        this.testEnabled = b;
        return this;
    }
    enableWrite(b) {
        this.writeEnabled = b;
        return this;
    }
    setCompareFunc(func) {
        this.compareFunc = func;
        return this;
    }
    setDepthBias(value) {
        this.depthBias = value;
        return this;
    }
    setDepthBiasSlopeScale(value) {
        this.depthBiasSlopeScale = value;
        return this;
    }
    _apply(gl) {
        if (this.testEnabled) {
            gl.enable(WebGLEnum.DEPTH_TEST);
            gl.depthFunc(this._compareFunc);
        } else {
            gl.disable(WebGLEnum.DEPTH_TEST);
        }
        gl.depthMask(this.writeEnabled);
        if (this.depthBias !== 0 || this.depthBiasSlopeScale !== 0) {
            gl.enable(gl.POLYGON_OFFSET_FILL);
            gl.polygonOffset(this.depthBiasSlopeScale, this.depthBias);
        } else {
            gl.disable(gl.POLYGON_OFFSET_FILL);
        }
    }
}
class WebGLStencilState extends WebGLRenderState {
    static _defaultState = new WebGLStencilState();
    static _currentState = null;
    enabled;
    writeMask;
    ref;
    readMask;
    _failOp;
    _failOpBack;
    _zFailOp;
    _zFailOpBack;
    _passOp;
    _passOpBack;
    _func;
    _funcBack;
    constructor(){
        super();
        this.enabled = false;
        this.failOp = this.failOpBack = 'keep';
        this.zFailOp = this.zFailOpBack = 'keep';
        this.passOp = this.passOpBack = 'keep';
        this.func = this.funcBack = 'always';
        this.ref = 0;
        this.writeMask = 0xffffffff;
        this.readMask = 0xffffffff;
    }
    clone() {
        const other = new WebGLStencilState();
        other.enable(this.enabled);
        other.setWriteMask(this.writeMask);
        other.setFrontOp(this.failOp, this.zFailOp, this.passOp);
        other.setBackOp(this.failOpBack, this.zFailOpBack, this.passOpBack);
        other.setFrontCompareFunc(this.func);
        other.setBackCompareFunc(this.funcBack);
        other.setReference(this.ref);
        other.setReadMask(this.readMask);
        return other;
    }
    get failOp() {
        return stencilOpInvMap[this._failOp];
    }
    set failOp(val) {
        this._failOp = stencilOpMap[val];
    }
    get failOpBack() {
        return stencilOpInvMap[this._failOpBack];
    }
    set failOpBack(val) {
        this._failOpBack = stencilOpMap[val];
    }
    get zFailOp() {
        return stencilOpInvMap[this._zFailOp];
    }
    set zFailOp(val) {
        this._zFailOp = stencilOpMap[val];
    }
    get zFailOpBack() {
        return stencilOpInvMap[this._zFailOpBack];
    }
    set zFailOpBack(val) {
        this._zFailOpBack = stencilOpMap[val];
    }
    get passOp() {
        return stencilOpInvMap[this._passOp];
    }
    set passOp(val) {
        this._passOp = stencilOpMap[val];
    }
    get passOpBack() {
        return stencilOpInvMap[this._passOpBack];
    }
    set passOpBack(val) {
        this._passOpBack = stencilOpMap[val];
    }
    get func() {
        return compareFuncInvMap[this._func];
    }
    set func(val) {
        this._func = compareFuncMap[val];
    }
    get funcBack() {
        return compareFuncInvMap[this._funcBack];
    }
    set funcBack(val) {
        this._funcBack = compareFuncMap[val];
    }
    enable(b) {
        this.enabled = b;
        return this;
    }
    setWriteMask(mask) {
        this.writeMask = mask;
        return this;
    }
    setFrontOp(fail, zfail, zpass) {
        this.failOp = fail;
        this.zFailOp = zfail;
        this.passOp = zpass;
        return this;
    }
    setBackOp(fail, zfail, zpass) {
        this.failOpBack = fail;
        this.zFailOpBack = zfail;
        this.passOpBack = zpass;
        return this;
    }
    setFrontCompareFunc(func) {
        this.func = func;
        return this;
    }
    setBackCompareFunc(func) {
        this.funcBack = func;
        return this;
    }
    setReference(ref) {
        this.ref = ref;
        return this;
    }
    setReadMask(mask) {
        this.readMask = mask;
        return this;
    }
    _apply(gl) {
        if (this.enabled) {
            gl.enable(WebGLEnum.STENCIL_TEST);
            gl.stencilMaskSeparate(WebGLEnum.FRONT, this.writeMask);
            gl.stencilMaskSeparate(WebGLEnum.BACK, this.writeMask);
            gl.stencilFuncSeparate(WebGLEnum.FRONT, this._func, this.ref, this.readMask);
            gl.stencilFuncSeparate(WebGLEnum.BACK, this._funcBack, this.ref, this.readMask);
            gl.stencilOpSeparate(WebGLEnum.FRONT, this._failOp, this._zFailOp, this._passOp);
            gl.stencilOpSeparate(WebGLEnum.BACK, this._failOpBack, this._zFailOpBack, this._passOpBack);
        } else {
            gl.disable(WebGLEnum.STENCIL_TEST);
        }
    }
}
class WebGLRenderStateSet {
    _gl;
    colorState;
    blendingState;
    rasterizerState;
    depthState;
    stencilState;
    constructor(gl){
        this._gl = gl;
        this.colorState = null;
        this.blendingState = null;
        this.rasterizerState = null;
        this.depthState = null;
        this.stencilState = null;
    }
    clone() {
        const newStateSet = new WebGLRenderStateSet(this._gl);
        newStateSet.colorState = this.colorState?.clone() ?? null;
        newStateSet.blendingState = this.blendingState?.clone() ?? null;
        newStateSet.rasterizerState = this.rasterizerState?.clone() ?? null;
        newStateSet.depthState = this.depthState?.clone() ?? null;
        newStateSet.stencilState = this.stencilState?.clone() ?? null;
        return newStateSet;
    }
    copyFrom(stateSet) {
        this.colorState = stateSet.colorState;
        this.blendingState = stateSet.blendingState;
        this.rasterizerState = stateSet.rasterizerState;
        this.depthState = stateSet.depthState;
        this.stencilState = stateSet.stencilState;
    }
    apply(force) {
        const gl = this._gl;
        if (this.colorState) {
            this.colorState.apply(gl, force);
        } else {
            WebGLColorState.applyDefaults(gl, force);
        }
        if (this.blendingState) {
            this.blendingState.apply(gl, force);
        } else {
            WebGLBlendingState.applyDefaults(gl, force);
        }
        if (this.rasterizerState) {
            this.rasterizerState.apply(gl, force);
        } else {
            WebGLRasterizerState.applyDefaults(gl, force);
        }
        if (this.depthState) {
            this.depthState.apply(gl, force);
        } else {
            WebGLDepthState.applyDefaults(gl, force);
        }
        if (this.stencilState) {
            this.stencilState.apply(gl, force);
        } else {
            WebGLStencilState.applyDefaults(gl, force);
        }
    }
    useColorState(state) {
        return this.colorState = state ?? this.colorState ?? new WebGLColorState();
    }
    defaultColorState() {
        this.colorState = null;
    }
    useBlendingState(state) {
        return this.blendingState = state ?? this.blendingState ?? new WebGLBlendingState();
    }
    defaultBlendingState() {
        this.blendingState = null;
    }
    useRasterizerState(state) {
        return this.rasterizerState = state ?? this.rasterizerState ?? new WebGLRasterizerState();
    }
    defaultRasterizerState() {
        this.rasterizerState = null;
    }
    useDepthState(state) {
        return this.depthState = state ?? this.depthState ?? new WebGLDepthState();
    }
    defaultDepthState() {
        this.depthState = null;
    }
    useStencilState(state) {
        return this.stencilState = state ?? this.stencilState ?? new WebGLStencilState();
    }
    defaultStencilState() {
        this.stencilState = null;
    }
    static applyDefaults(gl, force) {
        WebGLColorState.applyDefaults(gl, force);
        WebGLBlendingState.applyDefaults(gl, force);
        WebGLRasterizerState.applyDefaults(gl, force);
        WebGLDepthState.applyDefaults(gl, force);
        WebGLStencilState.applyDefaults(gl, force);
    }
}

export { WebGLBlendingState, WebGLColorState, WebGLDepthState, WebGLRasterizerState, WebGLRenderState, WebGLRenderStateSet, WebGLStencilState };
//# sourceMappingURL=renderstate_webgl.js.map
